import type { ProStudioContext } from './prostudio.xstate.types';

/** The three Channel Settings loads, in the order the page dispatches them. */
export type ChannelSettingsLoad =
  | 'ruleGroups'
  | 'apiCredentials'
  | 'liveAgentPlatforms';

/** How many times each load has failed, as the reader last saw it. */
export type ChannelSettingsLoadFailureCounts = Partial<
  Record<ChannelSettingsLoad, number>
>;

/**
 * Counts a failure of one of the Channel Settings loads. A SUCCESS records
 * nothing — see `isChannelSettingsLoadFailed`, where having the data outranks
 * any count.
 *
 * The page walks the three loads in sequence and gates each dispatch on "is my
 * slice still `null`?" — which, on its own, cannot tell "not started" apart
 * from "tried and failed". A failed flow leaves the slice `null` and
 * `botFlowError` hands the machine back to `idle` after 100ms, so the chain
 * effect re-dispatches the same load about ten times a second, each round
 * raising another error toast. Writing the outcome down is what ends that.
 *
 * `settled` is deliberately about DATA, not about the promise resolving: a flow
 * that resolves without a payload leaves the slice `null` too, and would spin
 * the same way.
 *
 * A monotonic COUNT rather than a flag, because this record is PROJECT-scoped
 * while the three loads it describes are dispatched from FOUR places: Channel
 * Settings and the Data Privacy / Reporting API Credentials / Live Agent
 * Platforms screens. Those three gate only on their own slice being `null`,
 * never read this record, and their effects depend on nothing a failure
 * changes — so they do not retry either. A flag raised from one of them
 * therefore outlived the page that saw it, and Channel Settings, opening
 * later, read it as "already settled, and it failed": it skipped its own
 * dispatch and rendered the permanent "Couldn't load … Reload the page"
 * without having tried once. A reader now remembers the count it saw when it
 * DISPATCHED a step and only owns what the count gained afterwards.
 *
 * A count and not a timestamp on purpose: `Date.now()` is wall-clock, so a
 * backwards clock step (NTP, a manual change) between the reader's snapshot
 * and the failure would read the reader's OWN failure as somebody else's and
 * re-open the ~10×/s loop this record exists to close. The same reasoning
 * produced `cellClipboardEpoch` and `expandedCellViewAutofocusNonce` in
 * `table-store`, which are counters for exactly this question.
 */
export const markChannelSettingsLoad = (
  ctx: ProStudioContext,
  load: ChannelSettingsLoad,
  settled: boolean
): void => {
  if (settled) {
    return;
  }

  const counts = ctx.channelSettingsLoadFailures;

  ctx.channelSettingsLoadFailures = {
    ...counts,
    [load]: (counts?.[load] ?? 0) + 1,
  };
};

/**
 * Whether a reader should treat a load as tried-and-failed.
 *
 * Three inputs, and each rules out one wrong answer:
 *
 *  - `loaded` — data present outranks any count. Without this a step that
 *    failed and then SUCCEEDED would keep its raised count and `BlockLoadState`,
 *    which gives `failed` priority over `loaded`, would render "Couldn't load …"
 *    over a block whose data is right there.
 *  - `countAtDispatch` — `undefined` means this reader has not asked for the
 *    step yet, so nothing that happened before can settle it. This is what makes
 *    a failure raised by ANOTHER screen — including one still in flight when the
 *    reader mounted — belong to that screen and not to this one.
 *  - the count having GROWN since that snapshot — a failure the reader's own
 *    dispatch produced. That is what stops the ~10×/s retry loop, because the
 *    loads are serialized through `idle`: once this reader has dispatched, every
 *    write to this step's count comes from its own flow.
 */
export const isChannelSettingsLoadFailed = (state: {
  loaded: boolean;
  failureCount: number | undefined;
  failureCountAtDispatch: number | undefined;
}): boolean =>
  !state.loaded &&
  state.failureCountAtDispatch != null &&
  (state.failureCount ?? 0) > state.failureCountAtDispatch;

/**
 * Which load the Channel Settings chain should dispatch next, or `null` when
 * there is nothing left to ask for.
 *
 * The three loads are SERIALIZED because the machine handles each event only in
 * `idle`, so firing them together drops the second and third. Walking them in
 * order means each step's gate has to answer "is this step DONE?" — and done
 * includes "tried and failed". Treating a failure as still-pending is what made
 * the page re-dispatch the same load about ten times a second for as long as it
 * stayed open, never reaching the steps behind it.
 *
 * Pure and total on purpose: the effect that owns the dispatching cannot be
 * unit-tested here (no RTL in this package), so the DECISION lives where a test
 * can reach it and the effect only carries it out.
 *
 * `isSaving` holds the whole chain while a channel mutation is in flight
 * (PSTUDIO-4253). The chain and the save wait in the SAME window: both are
 * idle-only, and this effect re-dispatches the moment the machine reaches
 * `idle`. A load that wins that race takes the machine straight back out of
 * `idle`, so the save — already queued in `awaitMachineIdle` — waits for a round
 * trip it did not need, and the user sees nothing happen for that much longer.
 * The remaining blocks can wait; the click the user just made cannot.
 */
export const nextChannelSettingsLoad = (state: {
  ruleGroupsLoaded: boolean;
  ruleGroupsFailed: boolean;
  apiCredentialsLoaded: boolean;
  apiCredentialsFailed: boolean;
  liveAgentLoaded: boolean;
  liveAgentFailed: boolean;
  isSaving?: boolean;
}): ChannelSettingsLoad | null => {
  if (state.isSaving) {
    return null;
  }
  if (!state.ruleGroupsLoaded && !state.ruleGroupsFailed) {
    return 'ruleGroups';
  }
  if (!state.apiCredentialsLoaded && !state.apiCredentialsFailed) {
    return 'apiCredentials';
  }
  if (!state.liveAgentLoaded && !state.liveAgentFailed) {
    return 'liveAgentPlatforms';
  }
  return null;
};
